import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { AuthContext } from '../context/AuthContext';
import { formatDateTimeDMY } from '../utils/dateUtils';

// Signs of tampering found on a loan's documents (loan.document_flags), for
// the back office only. The API adds them, like loan.calculator, on staff loan
// responses and never on the client's own, and a client never sees this. A
// flag does not stop the application: it is shown here for the officer to
// look into, and handled on the Flagged Documents page.
export const useLoanDocumentFlags = (loan) => {
  const { user } = useContext(AuthContext) || {};
  const perms = user?.permissions || [];
  const isStaff = perms.includes('*') || perms.includes('ViewLoans') || perms.includes('ManageLoans');
  const given = loan?.document_flags;
  const [fetched, setFetched] = useState(null);

  useEffect(() => {
    setFetched(null);
    if (!isStaff || !loan?.id || given !== undefined) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl(`/api/loans/${loan.id}`), { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setFetched(res.data?.document_flags || []);
      } catch {
        // Not available — no banner.
      }
    })();
    return () => { cancelled = true; };
  }, [isStaff, loan?.id, given]);

  if (!isStaff) return [];
  return (given !== undefined ? given : fetched) || [];
};

const STATUS_LABEL = { Open: 'To review', Tampered: 'Confirmed tampered' };

const DocumentFlagsBanner = ({ flags, className = '' }) => {
  const navigate = useNavigate();
  if (!Array.isArray(flags) || !flags.length) return null;
  const tampered = flags.some((f) => f.status === 'Tampered');
  const high = flags.some((f) => f.risk_level === 'high');
  const tone = tampered || high
    ? { box: 'border-rose-300 dark:border-rose-700/60 bg-rose-50 dark:bg-rose-900/20', strong: 'text-rose-800 dark:text-rose-200', soft: 'text-rose-700 dark:text-rose-300' }
    : { box: 'border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20', strong: 'text-amber-900 dark:text-amber-200', soft: 'text-amber-800 dark:text-amber-300' };
  return (
    <div className={`rounded-lg border-2 ${tone.box} p-4 ${className}`} role="alert">
      <p className={`text-sm font-bold ${tone.strong}`}>
        {tampered
          ? 'A document on this application was confirmed as tampered.'
          : `Possible document tampering on ${flags.length === 1 ? 'a document' : `${flags.length} documents`} — check before you go ahead.`}
      </p>
      <p className={`mt-1 text-xs ${tone.soft}`}>Back office only: the client is not told. The application was not stopped.</p>
      <ul className="mt-3 space-y-3">
        {flags.map((f) => (
          <li key={f.id} className={`text-sm ${tone.strong}`}>
            <div className="font-semibold">
              {f.document_type || 'Document'}{f.file_name ? ` — ${f.file_name}` : ''}
              <span className={`ml-2 text-xs font-medium ${tone.soft}`}>
                {f.risk_level === 'high' ? 'High' : 'Medium'} risk · {f.score}/100 · {STATUS_LABEL[f.status] || f.status}
                {f.created_at ? ` · ${formatDateTimeDMY(f.created_at)}` : ''}
              </span>
            </div>
            {Array.isArray(f.signals) && f.signals.length > 0 && (
              <ul className={`mt-1 list-disc pl-5 space-y-0.5 text-xs ${tone.soft}`}>
                {f.signals.map((s, i) => <li key={`${s.code}-${i}`}>{s.message}</li>)}
              </ul>
            )}
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={() => navigate('/document-flags')}
        className={`mt-3 text-xs font-semibold underline underline-offset-2 ${tone.strong}`}
      >
        Review in Flagged Documents
      </button>
    </div>
  );
};

export default DocumentFlagsBanner;
